/** Batch "Promote to staff" dialog for the Users grid (a custom `dialog(ctx)`
 *  batch action).
 *
 *  Promoting makes each selected user staff of an access group's tenant. Access
 *  groups are tenant-scoped, so the operator first picks WHICH operator's groups
 *  to choose from (defaulting to the grid's tenant filter), then an access group.
 *  A user can only be staff of a group in their franchise family, so when the
 *  selection may reach outside the group's tenant (`detectCrossTenant`) the only
 *  available behaviour is `ignore_others` — out-of-family users are skipped. We
 *  surface that as a single-option strategy note; submit fires the generic
 *  `/users/batch/` dispatch via `ctx.runStandard`. The server re-checks the
 *  cross-tenant condition and is authoritative (a 400 surfaces as a snackbar).
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { accessGroupsApi, type AccessGroupGlobalRow } from '@/api/accessGroups';
import { CrossTenantStrategyField } from '@/components/DataGrid/CrossTenantStrategyField';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

/** An access group is a hand-curated bundle — not matchable/creatable across
 *  tenants, so `ignore_others` is the only strategy (server-enforced). */
const ACCESS_GROUP_STRATEGIES = ['ignore_others'];

interface Props {
  ctx: BatchActionContext;
  /** The grid's current tenant filter (null when unfiltered). Used as the default
   *  source operator and to decide whether the selection is cross-tenant. */
  gridTenantId: number | null;
}

export function PromoteToStaffDialog({ ctx, gridTenantId }: Props) {
  // The operator whose access groups we pick from (defaults to the grid filter).
  const [sourceOp, setSourceOp] = useState<TenantPickerValue | null>(
    gridTenantId != null ? { id: gridTenantId, name: '' } : null,
  );
  const [group, setGroup] = useState<AccessGroupGlobalRow | null>(null);
  const [strategy, setStrategy] = useState<string | null>(null);

  const sourceOpId = sourceOp?.id ?? null;
  // The picker returns only the source operator's own groups, so the value's
  // tenant is the source operator.
  const crossTenant =
    sourceOpId != null &&
    detectCrossTenant(sourceOpId, ctx.selection, { gridTenantFilter: gridTenantId });

  const needsStrategy = crossTenant && !strategy;
  const canSubmit = sourceOpId != null && group != null && !needsStrategy;

  const submit = () => {
    if (!group) return;
    const params: Record<string, unknown> = { access_group: group.id };
    if (crossTenant && strategy) params.cross_tenant_strategy = strategy;
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Promote to staff</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Make {ctx.count.toLocaleString()} user(s) staff of an access group&apos;s tenant. This
            runs in the background. Guests outside the group&apos;s tenant are skipped.
          </Typography>

          {gridTenantId == null && (
            <TenantPicker
              value={sourceOp}
              onChange={(t) => {
                setSourceOp(t);
                setGroup(null);
                setStrategy(null);
              }}
            />
          )}

          <RemoteAutocomplete<AccessGroupGlobalRow>
            label="Access group"
            size="small"
            fetchOnEmpty
            value={group}
            getOptionLabel={(o) => o.name ?? `#${o.id}`}
            getOptionKey={(o) => o.id}
            queryFn={async (q) => {
              if (sourceOpId == null) return [];
              const res = await accessGroupsApi.globalList({
                tour_operator: sourceOpId,
                search: q || undefined,
                limit: 50,
              });
              return res.results;
            }}
            onChange={(v) => setGroup(v as AccessGroupGlobalRow | null)}
          />

          {crossTenant && (
            <CrossTenantStrategyField
              value={strategy}
              onChange={setStrategy}
              allowedStrategies={ACCESS_GROUP_STRATEGIES}
              noun="access group"
            />
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} users.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Promote
        </Button>
      </DialogActions>
    </Dialog>
  );
}
